import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Plus } from 'lucide-react';

import { AppShell } from '@/components/AppShell';
import { AlbumCoverCard } from '@/components/AlbumCoverCard';
import { CreateAlbumDialog } from '@/components/CreateAlbumDialog';
import { DateDoodle } from '@/components/DateDoodle';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserAlbums } from '@/hooks/useUserAlbums';
import { getPastelStripColor, seedFromHex } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

export default function MyAlbums() {
  const { user } = useCurrentUser();
  const { data: albums, isLoading } = useUserAlbums(user?.pubkey);
  const { hasBgImage } = useDittoThemeContext();
  const [createOpen, setCreateOpen] = useState(false);

  useSeoMeta({
    title: 'My Albums — Polaroids',
    description: 'Your photo albums on Polaroids.',
  });

  if (!user) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center py-20 text-center gap-6">
          <div className="bg-polaroid-white p-4 pb-12 border-2 border-dashed border-cream-dark shadow-sm w-48" style={{ transform: 'rotate(-1deg)' }}>
            <div className="aspect-[3/4] flex items-center justify-center bg-cream-dark/50">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="text-pol-text-secondary/40">
                <rect x="8" y="6" width="20" height="24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
                <rect x="20" y="12" width="20" height="24" fill="white" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </div>
            <p className="font-hand text-pol-text-secondary text-center mt-2 text-lg">Log in to see your albums</p>
          </div>
          <LoginArea className="max-w-60" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="py-6 space-y-5">
        {/* Page title */}
        <div className="flex items-center gap-3">
          <DateDoodle seed="my-albums-title" size={28} />
          <h1
            className={cn(
              'font-hand text-3xl font-bold text-pol-text',
              hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
            )}
            style={hasBgImage ? undefined : { backgroundColor: getPastelStripColor('my-albums-heading') }}
          >
            My Albums
          </h1>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-polaroid-white p-2 shadow-[0_2px_8px_rgba(45,27,78,0.08)]">
                <Skeleton className="aspect-[3/4] w-full skeleton-shimmer animate-shimmer" />
                <div className="pt-2 px-1 space-y-1.5">
                  <Skeleton className="h-4 w-3/4 skeleton-shimmer animate-shimmer" />
                  <Skeleton className="h-3 w-1/3 skeleton-shimmer animate-shimmer" />
                </div>
              </div>
            ))}
          </div>
        ) : albums && albums.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {albums.map((album, i) => (
              <AlbumCoverCard key={album.dTag} album={album} variant="grid" index={i} />
            ))}

            {/* New album card */}
            <button
              onClick={() => setCreateOpen(true)}
              className="relative bg-polaroid-white border-2 border-dashed border-pol-pink/30 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-[0.97] aspect-[3/4] flex flex-col items-center justify-center gap-2"
              style={{ transform: `rotate(${(seedFromHex('new-album', 0, 4) % 5) - 2}deg)` }}
            >
              {/* Hand-drawn plus sign */}
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="album-plus-grad" x1="0" y1="0" x2="36" y2="36" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#F0B8D0" />
                    <stop offset="50%" stopColor="#D4B8F0" />
                    <stop offset="100%" stopColor="#B8E0F0" />
                  </linearGradient>
                </defs>
                <path d="M18 6 C17.7 11, 18.4 14, 18 18 C17.7 22, 18.3 26, 18 30" stroke="url(#album-plus-grad)" strokeWidth="3.5" strokeLinecap="round" fill="none" />
                <path d="M6 18 C11 17.7, 14 18.4, 18 18 C22 17.7, 26 18.3, 30 18" stroke="url(#album-plus-grad)" strokeWidth="3.5" strokeLinecap="round" fill="none" />
              </svg>
              <span className="font-hand text-pol-text-secondary text-base">New Album</span>
            </button>
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center py-12 text-center gap-6">
            <div className="bg-polaroid-white p-6 border-2 border-dashed border-cream-dark/50 shadow-sm" style={{ transform: 'rotate(-1deg)' }}>
              <div className="flex items-center justify-center gap-3 mb-3">
                <DateDoodle seed="empty-albums-1" size={26} />
                <DateDoodle seed="empty-albums-2" size={22} />
              </div>
              <p className="font-hand text-xl text-pol-text mb-1">No albums yet</p>
              <p className="text-sm text-pol-text-secondary">
                Create your first album to organize your photos
              </p>
            </div>
            <button
              onClick={() => setCreateOpen(true)}
              className="relative bg-polaroid-white px-6 py-3 shadow-[0_3px_12px_rgba(45,27,78,0.12)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(45,27,78,0.15)] active:scale-[0.97]"
              style={{ transform: 'rotate(-0.8deg)' }}
            >
              <div
                className="absolute top-0 left-1/2 z-10 w-[80px] h-[18px] tape-rainbow"
                style={{ transform: 'translateX(-50%) translateY(-9px) rotate(1.5deg)' }}
              />
              <span className="font-hand text-xl font-bold text-pol-text flex items-center gap-2">
                <Plus size={20} />
                Create Album
              </span>
            </button>
          </div>
        )}
      </div>

      <CreateAlbumDialog open={createOpen} onOpenChange={setCreateOpen} />
    </AppShell>
  );
}
